<template>
  <div id="networkUsageDashboard">
    <div v-if="school.site_code !== null">
    <Header
      pageIcon="network_check"
      :header="header"
      :content="content"
      :sitecode="school.site_code"
    />
    <div class="contentSpacer">
    <v-alert
      type="info"
      border="left"
      colored-border
    >
      <b>Please select a date range of 7 days or less</b><br>
      Network Usage data is available up to 3 months prior to the current date. Due to the immensity of the data, we can only display 7 days of data at a time.
    </v-alert>
    </div>
    <v-container
      fluid
      style=""
      class="mb-0 pb-0 contentSpacer"
    >
      <v-card class="pa-5 mb-3 d-flex flex-row">
          <v-menu
            v-model="datePickerFromMenu"
            :close-on-content-click="false"
            :nudge-right="20"
            transition="scale-transition"
            offset-y
            max-width="290"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                :value="formattedFromDate"
                persistent-hint
                label="From"
                hide-details
                outlined
                prepend-inner-icon="event"
                readonly
                class="pr-5"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="fromDateSelected"
              @change="datePickerFromMenu = false"
              :min="minFromDate"
              :max="maxFromDate"
              no-title
              scrollable
            ></v-date-picker>
          </v-menu>
          <v-menu
            v-model="datePickerToMenu"
            :close-on-content-click="false"
            :nudge-right="20"
            transition="scale-transition"
            offset-y
            max-width="290"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                :value="formattedToDate"
                persistent-hint
                label="To"
                prepend-inner-icon="event"
                hide-details
                readonly
                outlined
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="toDateSelected"
              @change="datePickerToMenu = false"
              :min="minToDate"
              :max="maxToDate"
              no-title
            ></v-date-picker>
          </v-menu>
          <v-btn
            text
            v-if="resetRequired"
            large
            @click="handelResetToDefault"
            style="height: 57px;"
          >
            <span style="text-transform: capitalize; font-weight: normal;font-size: 16px;">Reset</span>
          </v-btn>
      </v-card>
    </v-container>

    <v-container
      fluid
      class="mb-0 pb-0 contentSpacer"
      v-if="allSchools && allSchools.length > 1"
    >
      <v-row justify="space-between">
        <v-col
          cols="12"
          lg="6"
        >
          <v-tabs
            slider-color="accent"
            color="accent"
            background-color="transparent"
          >
            <v-tab  v-for="(site, index) in this.allSchools"
                    :key="site.site_code"
                    @click="setCurrentSiteCode(site.site_code)">
              <div v-if="index===0">
                main site
              </div>
              <div v-else>
                sub site {{index}}
              </div>
            </v-tab>
          </v-tabs>
        </v-col>
      </v-row>
    </v-container>

    <v-container class="contentSpacer" fluid>
      <v-row>
        <v-col
          cols="12"
          sm="12"
          id="ictp-hc-network-traffic"
        >
          <NetworkTrafficWan
            v-if="currentSiteCode"
            :sitecode="currentSiteCode"
            :date-range="dateRange"
          />
<!--          <v-alert-->
<!--            v-else-->
<!--            :value="true"-->
<!--            color="warning"-->
<!--            icon="priority_high"-->
<!--            outlined-->
<!--          >-->
<!--            Please select a school from the school picker in the top right to display data for this page. Simply click-->
<!--            "Search for a school", then start typing the name of your desired school.-->
<!--          </v-alert>-->
        </v-col>
      </v-row>

      <v-row>
        <v-col
          cols="12"
          sm="12"
          id="ictp-hc-network-traffic-internet"
        >
          <NetworkTrafficInternet
            v-if="currentSiteCode"
            :sitecode="currentSiteCode"
            :date-range="dateRange"
          />
<!--          <v-alert-->
<!--            v-else-->
<!--            :value="true"-->
<!--            color="warning"-->
<!--            icon="priority_high"-->
<!--            outlined-->
<!--          >-->
<!--            Please select a school from the school picker in the top right to display data for this page. Simply click-->
<!--            "Search for a school", then start typing the name of your desired school.-->
<!--          </v-alert>-->
        </v-col>
      </v-row>
    </v-container>
    </div>
    <div class="contentSpacer" v-else>
      <EmptyState/>
    </div>
  </div>
</template>

<script>
  import {mapGetters, mapActions} from "vuex";
  import NetworkTrafficWan from "../highcharts/NetworkTrafficWan";
  import NetworkTrafficInternet from "../highcharts/NetworkTrafficInternet";
  import Header from "../simpleComponents/Header";
  import {format, parseISO, subDays, addDays, startOfDay, endOfDay, isValid,differenceInDays} from 'date-fns';
  import EmptyState from "../simpleComponents/EmptyState";

  export default {
    name: "NetworkUsageDashboard",
    components: {
        NetworkTrafficWan,
        NetworkTrafficInternet,
        Header,
        EmptyState
    },
    data() {
        return {
            currentSiteCode:  null,
            datePickerFromMenu: false,
            datePickerToMenu: false,
            fromDateSelected: format(new Date(), 'yyyy-MM-dd'),
            toDateSelected: format(new Date(), 'yyyy-MM-dd'),
            minFromDate: format(subDays(new Date(), 89), 'yyyy-MM-dd'),
            maxFromDate: format(new Date(), 'yyyy-MM-dd'),
            minToDate: format(new Date(), 'yyyy-MM-dd'),
            maxToDate: format(new Date(), 'yyyy-MM-dd'),
            header: "Network Usage",
            content: "The WAN is your school’s link to the department network that gives you internet access. Both department and non-department data flows through this link. Schools with Internet@Edge have an additional link only for non-department traffic such as YouTube, leaving the WAN link for only department traffic such as DOE apps."
        }
    },
    mounted() {
      this.fetchChildSchools({siteId: this.school.site_id});
      this.currentSiteCode= this.school? this.school.site_code: null;
    },
    computed: {
      ...mapGetters(["school","allSchools",]),
      formattedFromDate () {
        if (!this.fromDateSelected) return null;
        return format(parseISO(this.fromDateSelected), 'dd/MMM/yyyy');
      },
      formattedToDate () {
        if (!this.toDateSelected) return null;
        return format(parseISO(this.toDateSelected), 'dd/MMM/yyyy');
      },
      resetRequired(){
        let resetDate = format(new Date(), 'yyyy-MM-dd');
        if (!resetDate) return null;
        let resetFormat = format(parseISO(resetDate), 'dd/MMM/yyyy');
        if(resetFormat && (resetFormat !== this.formattedFromDate || resetFormat !== this.formattedToDate)){
          return true;
        }
        return false;
      },
      dateRange() {
          let dateRangeFilter = {
              startDate: '',
              endDate: '',
          };
          if (!this.fromDateSelected || !this.toDateSelected)  return dateRangeFilter;

          let selectedStartDate = parseISO(this.fromDateSelected);
          let selectedEndDate = parseISO(this.toDateSelected);
          if (isValid(selectedStartDate) && isValid(selectedEndDate)) {
              dateRangeFilter.startDate = startOfDay(selectedStartDate).toISOString();
              dateRangeFilter.endDate = endOfDay(selectedEndDate).toISOString();
          }
          return dateRangeFilter;
      },
    },
    methods: {
      ...mapActions(['fetchChildSchools']),
      handelResetToDefault() {
         this.fromDateSelected = format(new Date(), 'yyyy-MM-dd');
      },
      setCurrentSiteCode(siteCode) {
         this.currentSiteCode = siteCode;
      }
    },
    watch: {
      school: {
        handler: function () {
            this.setCurrentSiteCode(this.school.site_code);
            this.fetchChildSchools({siteId: this.school.site_id});
        },
        deep: true,
      },
      fromDateSelected(fromDate) {
         const curFromDate = parseISO(fromDate);
         if (isValid(curFromDate)) {
             this.minToDate = format(curFromDate, 'yyyy-MM-dd');
             this.toDateSelected = this.minToDate;
             const diffDays = differenceInDays(new Date(),curFromDate);
             if (diffDays >= 6 ) {
                 this.maxToDate = format(addDays(curFromDate, 6), 'yyyy-MM-dd');
             } else {
                 this.maxToDate = format(new Date(), 'yyyy-MM-dd');
             }
         }
      }
    }
  };
</script>

<style scoped>
  #networkUsageDashboard >>>  .v-alert {
    background-color: #ECEFF1 !important;
    border-color: #2E5299 !important;
  }

  #networkUsageDashboard >>> .v-alert__border {
    border-color: #2E5299 !important;
  }

  #networkUsageDashboard >>> .info--text {
    color: #2E5299 !important;
  }
</style>
